import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { EventInfoFieldset } from "@/components/EventInfoFieldset";
import { toast } from "sonner";

export default function HostCreateEvent() {
    const nav = useNavigate();
    const [categories, setCategories] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: "", description: "",
        event_date: "", start_time: "09:00", end_time: "15:00",
        location: "", city: "", state: "",
        parking_instructions: "", setup_instructions: "",
        attachments: [], images: [],
        total_booth_capacity: 10,
        total_employees: "",
        // Single source of truth for vendor categories + per-category capacity.
        // `desired_categories` is derived server-side from category_controls
        // (kept in sync on every save) and powers notification matching +
        // pricing snapshot, so callers don't need to send it explicitly.
        category_controls: [],
        event_info: {},
    });

    const submitPayload = () => ({
        ...form,
        total_employees: form.total_employees === "" || form.total_employees === null
            ? null
            : parseInt(form.total_employees, 10),
        desired_categories: form.category_controls.map((r) => r.category_id).filter(Boolean),
    });

    useEffect(() => { (async () => {
        const c = await api.get("/categories"); setCategories(c.data);
    })(); }, []);

    const toggleCat = (id) => {
        const has = form.category_controls.some((r) => r.category_id === id);
        const next = has
            ? form.category_controls.filter((r) => r.category_id !== id)
            : [...form.category_controls, { category_id: id, desired_count: 1, max_count: 1, waitlist_enabled: true, requires_approval_after_cap: true }];
        setForm({ ...form, category_controls: next });
    };

    const updateRow = (idx, patch) => {
        setForm((f) => ({
            ...f,
            category_controls: f.category_controls.map((r, i) => i === idx ? { ...r, ...patch } : r),
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await api.post("/events", submitPayload());
            toast.success("Event submitted for review!");
            nav(`/host/events/${res.data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save event");
        } finally { setBusy(false); }
    };

    return (
        <Shell>
            <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
                <div className="label-base">Host</div>
                <h1 className="font-heading text-3xl md:text-4xl font-extrabold">Create a new event</h1>
                <p className="text-brand-mute mt-2 text-sm">Submit your event for review. Once approved, vendors will be notified.</p>

                <form onSubmit={submit} className="mt-8 space-y-6" data-testid="event-form">
                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4">
                        <div>
                            <label className="label-base">Event name</label>
                            <input data-testid="ev-name" required className="input-base" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme HQ Spring Wellness Fair" />
                        </div>
                        <div>
                            <label className="label-base">Description</label>
                            <textarea data-testid="ev-desc" required rows={4} className="input-base !h-auto py-3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What employees can expect, the goal of the fair, and any themes." />
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                                <label className="label-base">Date</label>
                                <input data-testid="ev-date" required type="date" className="input-base" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
                            </div>
                            <div>
                                <label className="label-base">Start time</label>
                                <input data-testid="ev-start" required type="time" className="input-base" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                            </div>
                            <div>
                                <label className="label-base">End time</label>
                                <input data-testid="ev-end" required type="time" className="input-base" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div className="sm:col-span-2">
                                <label className="label-base">Venue / Address</label>
                                <input data-testid="ev-location" required className="input-base" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                            </div>
                            <div>
                                <label className="label-base">City</label>
                                <input data-testid="ev-city" className="input-base" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                                <label className="label-base">State</label>
                                <input data-testid="ev-state" className="input-base" placeholder="e.g. VA" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                            </div>
                            <div className="sm:col-span-2 flex items-end">
                                <p className="text-xs text-brand-mute">We'll geocode this address to notify nearby vendors automatically.</p>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label-base">Total booth capacity</label>
                                <input data-testid="ev-capacity" required type="number" min={1} className="input-base" value={form.total_booth_capacity} onChange={(e) => setForm({ ...form, total_booth_capacity: parseInt(e.target.value || "0", 10) })} />
                                <p className="text-xs text-brand-mute mt-1.5">How many vendor booths you have space for at this event.</p>
                            </div>
                            <div>
                                <label className="label-base">Total employees at this location</label>
                                <input data-testid="ev-total-employees" type="number" min={0} className="input-base" placeholder="e.g. 1200" value={form.total_employees} onChange={(e) => setForm({ ...form, total_employees: e.target.value.replace(/[^\d]/g, "") })} />
                                <p className="text-xs text-brand-mute mt-1.5">Total workforce at the worksite hosting the fair. Helps vendors estimate booth traffic.</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4">
                        <h3 className="font-heading font-bold">Event images</h3>
                        <p className="text-sm text-brand-mute">Add a hero image and supporting photos that will appear on the public marketplace and event page.</p>
                        <ImageUpload
                            value={form.images}
                            onChange={(imgs) => setForm({ ...form, images: imgs })}
                            max={6}
                            label="Event photos"
                            testIdPrefix="ev-img"
                        />
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4">
                        <h3 className="font-heading font-bold">Vendor categories & capacity</h3>
                        <p className="text-sm text-brand-mute">Pick every category you want at this fair. Each selected category gets its own row below where you can set how many booths you'll allow.</p>
                        <div className="flex flex-wrap gap-2">
                            {categories.map((c) => {
                                const active = form.category_controls.some((r) => r.category_id === c.id);
                                return (
                                    <button type="button" key={c.id} data-testid={`cat-${c.id}`} onClick={() => toggleCat(c.id)}
                                        className={`pill px-4 py-2 text-sm font-semibold border ${active ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-brand-line hover:border-brand-forest"}`}>
                                        {c.name}
                                    </button>
                                );
                            })}
                        </div>

                        {form.category_controls.length > 0 && (
                            <div className="rounded-lg border border-brand-line overflow-hidden" data-testid="vendor-mix-section">
                                <table className="w-full text-sm">
                                    <thead className="bg-brand-bone">
                                        <tr className="text-left text-xs uppercase tracking-wider text-brand-mute">
                                            <th className="px-3 py-2.5">Category</th>
                                            <th className="px-2 py-2.5">Capacity</th>
                                            <th className="px-2 py-2.5 text-center" title="When at capacity, additional applicants join a waitlist instead of being auto-rejected.">Waitlist</th>
                                            <th className="px-2 py-2.5 w-8"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.category_controls.map((r, idx) => {
                                            const cat = categories.find((c) => c.id === r.category_id);
                                            return (
                                                <tr key={`${r.category_id}-${idx}`} className="border-t border-brand-line" data-testid={`vmrow-${r.category_id}`}>
                                                    <td className="px-3 py-2 font-semibold">{cat?.name || r.category_id}</td>
                                                    <td className="px-2 py-2">
                                                        <input
                                                            data-testid={`vmrow-${r.category_id}-max`}
                                                            type="number" min={1}
                                                            className="input-base !h-9 w-20"
                                                            value={r.max_count}
                                                            onChange={(e) => {
                                                                const n = Math.max(1, parseInt(e.target.value || "1", 10));
                                                                // Keep desired_count == max_count by default so the
                                                                // single number drives both fields. Hosts who want to
                                                                // surface "desired vs max" can still patch via API.
                                                                updateRow(idx, { max_count: n, desired_count: n });
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 text-center">
                                                        <input
                                                            type="checkbox" className="h-4 w-4"
                                                            checked={r.waitlist_enabled}
                                                            onChange={(e) => updateRow(idx, { waitlist_enabled: e.target.checked })}
                                                            data-testid={`vmrow-${r.category_id}-waitlist`}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 text-right">
                                                        <button
                                                            type="button"
                                                            data-testid={`vmrow-${r.category_id}-remove`}
                                                            onClick={() => toggleCat(r.category_id)}
                                                            className="text-brand-coral text-xs font-semibold hover:underline"
                                                        >Remove</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4">
                        <h3 className="font-heading font-bold">Logistics (optional)</h3>
                        <div>
                            <label className="label-base">Parking instructions</label>
                            <textarea data-testid="ev-parking" rows={2} className="input-base !h-auto py-3" value={form.parking_instructions} onChange={(e) => setForm({ ...form, parking_instructions: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-base">Setup instructions</label>
                            <textarea data-testid="ev-setup" rows={2} className="input-base !h-auto py-3" value={form.setup_instructions} onChange={(e) => setForm({ ...form, setup_instructions: e.target.value })} />
                        </div>
                    </div>

                    {/* Insurance + workforce + venue logistics — all optional, helps
                        vendors tailor screenings/giveaways to the audience. */}
                    <div className="rounded-xl border border-brand-line bg-white p-6">
                        <EventInfoFieldset
                            value={form.event_info}
                            onChange={(next) => setForm({ ...form, event_info: next })}
                            testidPrefix="host-create-event-info"
                        />
                    </div>

                    <div className="flex justify-end gap-3">
                        <button type="button" onClick={() => nav("/host")} className="pill px-5 py-3 font-semibold border border-brand-line bg-white hover:bg-brand-bone">Cancel</button>
                        <button type="submit" data-testid="ev-submit" disabled={busy} className="pill btn-coral px-6 py-3 font-semibold disabled:opacity-50">
                            {busy ? "Submitting…" : "Submit for review"}
                        </button>
                    </div>
                </form>
            </div>
        </Shell>
    );
}
